/**
 * Sample Routine — public, no-auth, no-selfie marketing experience.
 *
 * A 4-step guided tour that shows a prospective user *exactly* what
 * they'd get if they signed up:
 *   1. Example face analysis (with realistic-looking traits)
 *   2. Example makeup routine (5 steps)
 *   3. Example product recommendations (3 picks, real brand names)
 *   4. Example AR coaching cues (3 voiced lines from Trouvé)
 *
 * Then a hero "Get My Personalized Routine" CTA that bounces into the
 * real signup flow (/login → /start).
 *
 * Analytics:
 *   • `sample_routine_opened` fires once on mount
 *   • `sample_routine_completed` fires once when the user advances
 *     past the AR coaching step (the last preview section)
 *   • `sample_routine_cta_clicked` fires when they hit the final CTA
 *
 * Stealth Mode: `track()` early-returns under stealth, so closeted
 * users get the full experience with zero events emitted. A small
 * banner at the top lets them know we know they're in stealth.
 *
 * All copy goes through i18n — see locales/{en,es,fr,pt}.json under
 * the `sampleRoutine` namespace.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Eye,
  Palette,
  Camera,
  Mic,
  ShieldCheck,
  Check,
  ChevronRight,
} from "lucide-react";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
// TrouveHeroVideo import removed Feb 8 2026 — the Sora 2 hero
// videos were pulled per founder direction.

const SECTIONS = ["analysis", "routine", "products", "coaching"];

const Pill = ({ children, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-full bg-[#9B6A6C]/10 text-[#9B6A6C] px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase"
  >
    {children}
  </span>
);

const StepDot = ({ active, done }) => (
  <span
    className={`h-1.5 rounded-full transition-all duration-500 ${
      active ? "w-8 bg-[#9B6A6C]" : done ? "w-3 bg-[#9B6A6C]/60" : "w-3 bg-[#E5D9D4]"
    }`}
  />
);

const SampleRoutine = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..3 = sections; advancing past 3 hits CTA screen
  const sectionKey = SECTIONS[step];

  // Fire `sample_routine_opened` exactly once when the user lands.
  // ── Analytics events: the page is the Meet-Trouvé demo (Task 2 rebrand).
  // We emit BOTH the new `trouve_demo_*` names AND the legacy
  // `sample_routine_*` names so:
  //   1. Yesterday's analytics_events history continues to feed the
  //      sample-routine funnel panel (existing dashboard widget),
  //   2. The new Trouvé funnel panel reads the new event names.
  // Once we've confirmed the new dashboard is correct, we'll drop the
  // legacy emit in a follow-up.
  useEffect(() => {
    track("trouve_demo_started");
    track("sample_routine_opened");
    // First-party (stealth-aware) — privacy-safe funnel
    trackFP("sample_routine_view");
  }, []);

  useEffect(() => {
    if (step === SECTIONS.length - 1) {
      track("trouve_demo_completed");
      track("sample_routine_completed");
    }
  }, [step]);

  const onCta = () => {
    // Both events fire from the button's onClick now (each with its own
    // `source`). This function just navigates so the footer/inline
    // buttons can emit different `source` props before redirect.
    navigate("/login?intent=signup");
  };

  const fireCtaEvents = (source) => {
    track("trouve_demo_to_signup", { source });
    track("sample_routine_cta_clicked", { source });
  };

  // ── Mock data — kept inline (not a separate fixture file) so all the
  // sample content lives next to the rendering logic. Translations
  // happen via t() at the render site below.
  const analysisTraits = useMemo(
    () => [
      { key: "undertone",   value: t("sampleRoutine.analysis.undertoneValue") },
      { key: "mstBucket",   value: t("sampleRoutine.analysis.mstBucketValue") },
      { key: "beardShadow", value: t("sampleRoutine.analysis.beardShadowValue") },
      { key: "sensitivity", value: t("sampleRoutine.analysis.sensitivityValue") },
    ],
    [t],
  );

  const routineSteps = useMemo(
    () => [
      { id: 1, durationMin: 2, color: "#F5DCD7" },
      { id: 2, durationMin: 3, color: "#EAC9C2" },
      { id: 3, durationMin: 2, color: "#D5A8A4" },
      { id: 4, durationMin: 4, color: "#C68F92" },
      { id: 5, durationMin: 2, color: "#9B6A6C" },
    ],
    [],
  );

  const products = useMemo(
    () => [
      { id: "p1", brand: "Fenty Beauty",     priceBand: "$36",  badge: "match" },
      { id: "p2", brand: "About-Face",       priceBand: "$22",  badge: "allergyFlag" },
      { id: "p3", brand: "Charlotte Tilbury", priceBand: "$42", badge: "fanFav" },
    ],
    [],
  );

  const coachingCues = useMemo(
    () => [
      { id: "c1", icon: Mic },
      { id: "c2", icon: Eye },
      { id: "c3", icon: Sparkles },
    ],
    [],
  );

  const goNext = () => {
    if (step < SECTIONS.length - 1) setStep((s) => s + 1);
    else {
      fireCtaEvents("footer_cta");
      onCta();
    }
  };
  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] text-[#2C2123]"
      data-testid="sample-routine-page"
    >
      {/* ───── Top bar ───── */}
      <div className="px-5 sm:px-8 pt-6 pb-4 flex items-center justify-between max-w-5xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition"
          data-testid="sample-routine-back-home"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("sampleRoutine.backHome")}
        </Link>
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#9B6A6C] font-semibold">
          {t("sampleRoutine.eyebrow")}
        </span>
      </div>

      {/* ───── Trouvé host band (Task 2 rebrand) ─────
          Makes Trouvé the host of every step of this demo. Same
          gradient brandmark as the homepage MeetTrouve section so
          the character is unmistakable across surfaces. */}
      <div className="px-5 sm:px-8 max-w-5xl mx-auto mb-4">
        <div
          className="flex items-center gap-3 bg-white/70 border border-[#EFE3DC] rounded-2xl px-4 py-3"
          data-testid="sample-routine-trouve-host"
        >
          <div
            className="shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-serif text-lg text-white shadow-md"
            style={{ background: "linear-gradient(135deg, #D5A8A4, #9B6A6C)" }}
            aria-hidden="true"
          >
            T
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-[15px] text-[#2C2123] leading-snug">
              {t("sampleRoutine.hostBand.line")}
            </div>
          </div>
        </div>
      </div>

      {/* ───── Step indicator ───── */}
      <div className="px-5 sm:px-8 max-w-5xl mx-auto">
        <div className="flex items-center gap-2" data-testid="sample-routine-step-indicator">
          {SECTIONS.map((s, i) => (
            <StepDot key={s} active={i === step} done={i < step} />
          ))}
          <span className="ml-3 text-xs text-[#7A6B6E]">
            {t("sampleRoutine.stepLabel", {
              current: step + 1,
              total: SECTIONS.length,
              section: t(`sampleRoutine.sections.${sectionKey}`),
            })}
          </span>
        </div>
      </div>

      {/* ───── Main panel ───── */}
      <main className="px-5 sm:px-8 max-w-5xl mx-auto pb-32 pt-8">
        <AnimatePresence mode="wait">
          <motion.section
            key={sectionKey}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.32, ease: "easeOut" }}
            data-testid={`sample-routine-section-${sectionKey}`}
          >
            {sectionKey === "analysis" && (
              <AnalysisSection traits={analysisTraits} t={t} />
            )}
            {sectionKey === "routine" && (
              <RoutineSection steps={routineSteps} t={t} />
            )}
            {sectionKey === "products" && (
              <ProductsSection products={products} t={t} />
            )}
            {sectionKey === "coaching" && (
              <CoachingSection cues={coachingCues} t={t} onCta={onCta} fireCtaEvents={fireCtaEvents} />
            )}
          </motion.section>
        </AnimatePresence>
      </main>

      {/* ───── Sticky footer nav ───── */}
      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/95 to-transparent pt-8 pb-5 px-5 sm:px-8 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#E5D9D4] text-sm text-[#5C4B51] hover:border-[#9B6A6C]/60 hover:text-[#9B6A6C] disabled:opacity-30 disabled:cursor-not-allowed transition"
            data-testid="sample-routine-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("sampleRoutine.back")}
          </button>
          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D5A8A4] to-[#9B6A6C] text-white text-sm font-semibold shadow-[0_8px_24px_-8px_rgba(155,106,108,0.55)] hover:shadow-[0_12px_30px_-8px_rgba(155,106,108,0.75)] transition"
            data-testid={step < SECTIONS.length - 1 ? "sample-routine-next-btn" : "sample-routine-cta-btn"}
          >
            {step < SECTIONS.length - 1
              ? t("sampleRoutine.next")
              : t("sampleRoutine.cta")}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Section · Face Analysis ───────────────────────────────────────────
const AnalysisSection = ({ traits, t }) => (
  <div>
    <Pill testid="sample-routine-analysis-pill">
      <Eye className="h-3 w-3" /> {t("sampleRoutine.analysis.pill")}
    </Pill>
    <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-2 leading-tight">
      {t("sampleRoutine.analysis.heading")}
    </h1>
    <p className="text-[#5C4B51] text-base sm:text-lg max-w-2xl mb-8">
      {t("sampleRoutine.analysis.body")}
    </p>
    <div className="grid sm:grid-cols-2 gap-3">
      {traits.map((trait) => (
        <div
          key={trait.key}
          className="bg-white border border-[#EFE3DC] rounded-2xl p-5"
          data-testid={`sample-routine-analysis-${trait.key}`}
        >
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#9B6A6C] font-semibold">
            {t(`sampleRoutine.analysis.label.${trait.key}`)}
          </div>
          <div className="font-serif text-2xl mt-1 text-[#2C2123]">
            {trait.value}
          </div>
          <p className="text-sm text-[#5C4B51] mt-2 leading-relaxed">
            {t(`sampleRoutine.analysis.detail.${trait.key}`)}
          </p>
        </div>
      ))}
    </div>
    <div className="mt-6 text-xs text-[#7A6B6E] italic" data-testid="sample-routine-sample-disclaimer">
      {t("sampleRoutine.sampleDisclaimer")}
    </div>
  </div>
);

// ─── Section · Routine Steps ────────────────────────────────────────────
const RoutineSection = ({ steps, t }) => (
  <div>
    <Pill testid="sample-routine-routine-pill">
      <Palette className="h-3 w-3" /> {t("sampleRoutine.routine.pill")}
    </Pill>
    <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-2 leading-tight">
      {t("sampleRoutine.routine.heading")}
    </h1>
    <p className="text-[#5C4B51] text-base sm:text-lg max-w-2xl mb-8">
      {t("sampleRoutine.routine.body", { total: steps.reduce((acc, s) => acc + s.durationMin, 0) })}
    </p>
    <ol className="space-y-3" data-testid="sample-routine-routine-list">
      {steps.map((s, i) => (
        <li
          key={s.id}
          className="bg-white border border-[#EFE3DC] rounded-2xl p-4 sm:p-5 flex items-start gap-4"
          data-testid={`sample-routine-routine-step-${s.id}`}
        >
          <div
            className="shrink-0 h-12 w-12 rounded-full flex items-center justify-center font-serif text-lg text-white"
            style={{ background: `linear-gradient(135deg, ${s.color}, #9B6A6C)` }}
          >
            {i + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h3 className="font-semibold text-[#2C2123] text-base">
                {t(`sampleRoutine.routine.step.${s.id}.title`)}
              </h3>
              <span className="text-[11px] text-[#7A6B6E] font-mono">
                {t("sampleRoutine.routine.duration", { mins: s.durationMin })}
              </span>
            </div>
            <p className="text-sm text-[#5C4B51] leading-relaxed">
              {t(`sampleRoutine.routine.step.${s.id}.detail`)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

// ─── Section · Product Recommendations ──────────────────────────────────
const ProductsSection = ({ products, t }) => (
  <div>
    <Pill testid="sample-routine-products-pill">
      <ShieldCheck className="h-3 w-3" /> {t("sampleRoutine.products.pill")}
    </Pill>
    <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-2 leading-tight">
      {t("sampleRoutine.products.heading")}
    </h1>
    <p className="text-[#5C4B51] text-base sm:text-lg max-w-2xl mb-8">
      {t("sampleRoutine.products.body")}
    </p>
    <div className="grid sm:grid-cols-3 gap-3" data-testid="sample-routine-products-grid">
      {products.map((p) => (
        <div
          key={p.id}
          className="bg-white border border-[#EFE3DC] rounded-2xl p-4 sm:p-5"
          data-testid={`sample-routine-product-${p.id}`}
        >
          <div
            className="h-32 -mx-5 -mt-5 mb-4 rounded-t-2xl bg-gradient-to-br from-[#F5DCD7] via-[#EAC9C2] to-[#D5A8A4] flex items-center justify-center"
            aria-hidden="true"
          >
            <Sparkles className="h-9 w-9 text-white/80" />
          </div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#9B6A6C] font-semibold">
            {p.brand}
          </div>
          <h3 className="font-serif text-xl mt-1 mb-1">
            {t(`sampleRoutine.products.item.${p.id}.name`)}
          </h3>
          <p className="text-sm text-[#5C4B51] mb-3 leading-relaxed">
            {t(`sampleRoutine.products.item.${p.id}.why`)}
          </p>
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#7A6B6E] font-medium">{p.priceBand}</span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
                p.badge === "match"        ? "bg-emerald-50  text-emerald-700"
              : p.badge === "allergyFlag"  ? "bg-amber-50    text-amber-700"
              :                              "bg-[#9B6A6C]/10 text-[#9B6A6C]"
              }`}
            >
              <Check className="h-3 w-3" />
              {t(`sampleRoutine.products.badge.${p.badge}`)}
            </span>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-6 text-xs text-[#7A6B6E] flex items-center gap-1.5">
      <ShieldCheck className="h-3.5 w-3.5 text-[#9B6A6C]" />
      {t("sampleRoutine.products.allergenNote")}
    </div>
  </div>
);

// ─── Section · AR Coaching ──────────────────────────────────────────────
const CoachingSection = ({ cues, t, onCta, fireCtaEvents }) => (
  <div>
    <Pill testid="sample-routine-coaching-pill">
      <Camera className="h-3 w-3" /> {t("sampleRoutine.coaching.pill")}
    </Pill>
    <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-2 leading-tight">
      {t("sampleRoutine.coaching.heading")}
    </h1>
    <p className="text-[#5C4B51] text-base sm:text-lg max-w-2xl mb-8">
      {t("sampleRoutine.coaching.body")}
    </p>

    {/* AR demo viewfinder — faux-landmark dots over a soft gradient.
        The Sora 2 video that used to sit on top of this was removed
        Feb 8 2026 per founder direction ("all sora videos need to
        be gone af"). The dot pattern was always the canonical
        fallback; now it stands alone, which the founder explicitly
        preferred over generative AI footage. */}
    <div
      className="relative rounded-3xl overflow-hidden aspect-[16/9] bg-gradient-to-br from-[#2C2123] via-[#4A363A] to-[#9B6A6C] mb-6 flex items-end justify-center p-6"
      data-testid="sample-routine-ar-viewfinder"
    >
      <div className="absolute inset-0 pointer-events-none opacity-70">
        {[[40,30],[60,30],[50,42],[42,55],[58,55],[50,65]].map(([x,y], i) => (
          <span
            key={i}
            className="absolute h-2 w-2 rounded-full bg-[#F5DCD7]/80"
            style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)", boxShadow: "0 0 12px rgba(245,220,215,0.6)" }}
          />
        ))}
      </div>
      <div className="relative bg-white/95 backdrop-blur rounded-full px-4 py-2 text-xs sm:text-sm font-medium text-[#2C2123] inline-flex items-center gap-2 shadow-lg z-10">
        <Mic className="h-3.5 w-3.5 text-[#9B6A6C]" />
        {t("sampleRoutine.coaching.demoCaption")}
      </div>
    </div>

    <ul className="space-y-3" data-testid="sample-routine-coaching-cues">
      {cues.map((c) => {
        const Icon = c.icon;
        return (
          <li
            key={c.id}
            className="bg-white border border-[#EFE3DC] rounded-2xl p-4 sm:p-5 flex items-start gap-4"
            data-testid={`sample-routine-coaching-cue-${c.id}`}
          >
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#9B6A6C]/12 text-[#9B6A6C] flex items-center justify-center">
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#2C2123] text-sm">
                {t(`sampleRoutine.coaching.cue.${c.id}.title`)}
              </h3>
              <p className="text-sm text-[#5C4B51] mt-1 leading-relaxed italic">
                &ldquo;{t(`sampleRoutine.coaching.cue.${c.id}.voiceLine`)}&rdquo;
              </p>
            </div>
          </li>
        );
      })}
    </ul>

    {/* Inline CTA at the end of the final section. The sticky footer
        also has a CTA button — both fire the same `sample_routine_cta_clicked`
        event with different `source` props so we can A/B which one
        actually converts. */}
    <div className="mt-10 bg-gradient-to-br from-[#2C2123] to-[#4A363A] text-white rounded-3xl p-7 sm:p-9">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#D5A8A4] font-semibold mb-3">
        {t("sampleRoutine.finalCta.eyebrow")}
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">
        {t("sampleRoutine.finalCta.heading")}
      </h2>
      <p className="text-white/75 mb-6 max-w-lg leading-relaxed">
        {t("sampleRoutine.finalCta.body")}
      </p>
      <button
        type="button"
        onClick={() => {
          fireCtaEvents("inline_final");
          onCta();
        }}
        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#F5DCD7] text-[#2C2123] font-semibold hover:bg-white transition shadow-lg"
        data-testid="sample-routine-final-cta-inline"
      >
        {t("sampleRoutine.cta")}
        <ChevronRight className="h-4 w-4" />
      </button>
      <div className="mt-4 text-xs text-white/55">
        {t("sampleRoutine.finalCta.reassure")}
      </div>
    </div>
  </div>
);

export default SampleRoutine;
